// @owners { team: patients }
import { Body, Button, Card, LgSpacing, LoadingEmptyState, SecondaryPage, XlPadding } from '@alto/design-system';
import { type ApiResponseRow } from '@alto/design-system/src/components/inputs/src/InputAutocomplete/types';
import { AllergiesInput, MedicalConditionsInput, useQueryMedicalInfoConfigs } from '@alto/features';
import React from 'react';
import { useController, useFormContext } from 'react-hook-form';
import getLoading from '~shared/features/ui/selectors/getLoading';
import { useSelectorShared } from '~shared/store';
import { OnboardingHeader } from '~web/features/onboarding/components/redesign/OnboardingHeader';
import { OnboardingNavBarLogo } from '~web/features/onboarding/components/redesign/OnboardingNavBarLogo';

export type MedicalInfoValues = {
  allergens: string[];
  medical_conditions: string;
  hasAllergens?: boolean;
};

type Props = {
  onSave: (values: MedicalInfoValues) => void;
};

export const OnboardingMedicalInfoPage = ({ onSave }: Props) => {
  const { updateOnboardingUserLoading, fetchUsersLoading } = useSelectorShared(getLoading);
  const loading = updateOnboardingUserLoading || fetchUsersLoading;
  const { headerBackgroundColor, headerLogoUrl, isFetching, partnershipName } = useQueryMedicalInfoConfigs();

  const {
    getValues,
    handleSubmit,
    control,
    formState: { submitCount },
  } = useFormContext<MedicalInfoValues>();

  // the BE handles parsing free-form text allergies and structured allergens
  // submit all allergies through allergens field
  const allergensController = useController({
    control,
    name: 'allergens',
    rules: {
      validate: (value) => {
        return getValues('hasAllergens') && value.length === 0 ? 'Allergies cannot be blank' : true;
      },
    },
  });
  // controller to track whether yes/no selection for error reporting
  const hasAllergensController = useController({
    control,
    name: 'hasAllergens',
  });

  const medicalConditionsController = useController({
    control,
    name: 'medical_conditions',
    rules: { required: 'Medical conditions cannot be blank', minLength: 1 },
  });

  const handleAllergiesChange = (allergies: ApiResponseRow<string>[]) => {
    const value = allergies.map((allergy) => allergy.value);
    allergensController.field.onChange(value);
  };

  if (isFetching) {
    return (
      <XlPadding>
        <LoadingEmptyState />
      </XlPadding>
    );
  }

  return (
    <SecondaryPage
      withoutWebFramingElementHeights
      onDismiss={undefined}
      dismissIcon={undefined}
      buttons={[
        <Button
          type="primary"
          key="continue"
          label="Continue"
          onPress={handleSubmit(onSave)}
        />,
      ]}
      footerPlacementContext="webScreenWithoutPolicyFooter"
      headerBackgroundColor={headerBackgroundColor}
      HeaderContent={
        <OnboardingHeader
          title="Health questions"
          subtitle={
            <Body center>
              For your safety, our pharmacists review this information before sending any medication out the door.
            </Body>
          }
        />
      }
      NavBarCenterContent={
        <OnboardingNavBarLogo
          illustrationSrc={headerLogoUrl}
          partnershipName={partnershipName}
        />
      }
    >
      {loading ? (
        <LoadingEmptyState />
      ) : (
        <Card>
          <LgSpacing />
          <AllergiesInput
            allergies={allergensController.field.value ?? undefined}
            error={allergensController.fieldState.error?.message}
            isTouched={allergensController.fieldState.isTouched}
            handleChange={handleAllergiesChange}
            handleButtonChange={hasAllergensController.field.onChange}
            hasSubmitted={submitCount > 0}
          />
          <MedicalConditionsInput
            medicalConditions={medicalConditionsController.field.value}
            handleChange={medicalConditionsController.field.onChange}
            error={medicalConditionsController.fieldState.error?.message}
            isTouched={medicalConditionsController.fieldState.isTouched}
            hasSubmitted={submitCount > 0}
          />
        </Card>
      )}
    </SecondaryPage>
  );
};
